import React from 'react';
import Article from '../components/Article';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CodeBlock from '../components/CodeBlock';

function KubernetesBasics() {
  const articleData = {
    id: 8,
    image: "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1200&q=80",
    category: "DevOps",
    title: "Kubernetes Basics: A Comprehensive Guide for Beginners",
    excerpt: "Learn the fundamental concepts of Kubernetes and how it can revolutionize your container orchestration strategy.",
    tags: ["Kubernetes", "DevOps", "Containers", "Docker", "Cloud Native"],
    content: (
      <>
        <p>
          Kubernetes (often abbreviated as K8s) has emerged as the de facto standard for container orchestration 
          in modern cloud-native applications. If you're looking to understand what Kubernetes is, why it matters, 
          and how to get started with it, you've come to the right place. This guide will walk you through the 
          essential concepts, components, and basic operations of Kubernetes.
        </p>
        
        <h2>What is Kubernetes?</h2>
        <p>
          Kubernetes is an open-source platform designed to automate deploying, scaling, and operating 
          application containers. Originally developed by Google and now maintained by the Cloud Native 
          Computing Foundation (CNCF), Kubernetes provides a framework to run distributed systems resiliently.
        </p>
        
        <p>
          At its core, Kubernetes solves a fundamental problem: running containers across multiple machines 
          while providing tools to deploy applications, scale as needed, manage changes to existing applications, 
          and optimize resource usage across your infrastructure.
        </p>
        
        <h2>Why Use Kubernetes?</h2>
        <p>
          Before diving into how Kubernetes works, it's worth understanding why it has become so essential 
          in modern application deployment:
        </p>
        
        <ul>
          <li><strong>Automated Scaling:</strong> Kubernetes can automatically scale your application up or down based on resource usage or custom metrics.</li>
          <li><strong>Self-healing:</strong> It automatically restarts containers that fail, replaces containers when nodes die, and kills containers that don't respond to health checks.</li>
          <li><strong>Service Discovery and Load Balancing:</strong> Kubernetes can expose a container using a DNS name or IP address and distribute network traffic to maintain deployment stability.</li>
          <li><strong>Automated Rollouts and Rollbacks:</strong> You can describe the desired state of your deployed containers, and Kubernetes will change the actual state to match, at a controlled rate.</li>
          <li><strong>Secret and Configuration Management:</strong> Deploy and update secrets and application configuration without rebuilding your image.</li>
          <li><strong>Storage Orchestration:</strong> Automatically mount storage systems of your choice, whether local or cloud providers.</li>
        </ul>
        
        <h2>Core Kubernetes Concepts</h2>
        <p>
          Understanding Kubernetes starts with grasping its basic building blocks and architecture.
        </p>
        
        <h3>Kubernetes Architecture</h3>
        <p>
          A Kubernetes cluster consists of two main components:
        </p>
        
        <ol className="workflow-steps">
          <li><strong>Control Plane:</strong> The control plane manages the worker nodes and the Pods in the cluster.</li>
          <li><strong>Worker Nodes:</strong> These machines run your applications and cloud workflows.</li>
        </ol>
        
        <p>
          The control plane makes global decisions about the cluster (like scheduling), as well as detecting and 
          responding to cluster events. It consists of components like:
        </p>
        
        <ul>
          <li><strong>API Server:</strong> The front end of the Kubernetes control plane that exposes the Kubernetes API.</li>
          <li><strong>etcd:</strong> A consistent and highly-available key-value store used as Kubernetes' backing store for all cluster data.</li>
          <li><strong>Scheduler:</strong> Watches for newly created Pods with no assigned node and selects a node for them to run on.</li>
          <li><strong>Controller Manager:</strong> Runs controller processes that regulate the state of the cluster.</li>
          <li><strong>Cloud Controller Manager:</strong> Interfaces with your cloud provider to manage resources like load balancers and storage.</li>
        </ul>
        
        <p>
          Each worker node contains:
        </p>
        
        <ul>
          <li><strong>Kubelet:</strong> An agent that ensures containers are running in a Pod.</li>
          <li><strong>Container Runtime:</strong> The software responsible for running containers (like Docker, containerd, or CRI-O).</li>
          <li><strong>Kube-proxy:</strong> A network proxy that maintains network rules on nodes to allow network communication to your Pods.</li>
        </ul>
        
        <h3>Key Kubernetes Objects</h3>
        <p>
          Kubernetes objects are persistent entities in the Kubernetes system that represent the state of your cluster.
          Here are the most fundamental ones:
        </p>
        
        <h4>1. Pods</h4>
        <p>
          A Pod is the smallest deployable unit in Kubernetes. It represents a single instance of a running process 
          in your cluster and can contain one or more containers. Containers within a Pod share networking and storage resources.
        </p>
        
        <CodeBlock language="yaml">
{`# Example Pod manifest (pod.yaml)
apiVersion: v1
kind: Pod
metadata:
  name: nginx-pod
  labels:
    app: nginx
spec:
  containers:
  - name: nginx
    image: nginx:1.21
    ports:
    - containerPort: 80`}
        </CodeBlock>
        
        <h4>2. Deployments</h4>
        <p>
          Deployments manage the creation and updating of replica sets (a group of identical Pods). 
          They're perfect for stateless applications and provide declarative updates to Pods.
        </p>
        
        <CodeBlock language="yaml">
{`# Example Deployment manifest (deployment.yaml)
apiVersion: apps/v1
kind: Deployment
metadata:
  name: nginx-deployment
  labels:
    app: nginx
spec:
  replicas: 3
  selector:
    matchLabels:
      app: nginx
  template:
    metadata:
      labels:
        app: nginx
    spec:
      containers:
      - name: nginx
        image: nginx:1.21
        ports:
        - containerPort: 80`}
        </CodeBlock>
        
        <h4>3. Services</h4>
        <p>
          Services define a logical set of Pods and a policy to access them. They enable loose coupling 
          between dependent Pods and provide stable endpoints for communication.
        </p>
        
        <CodeBlock language="yaml">
{`# Example Service manifest (service.yaml)
apiVersion: v1
kind: Service
metadata:
  name: nginx-service
spec:
  selector:
    app: nginx
  ports:
  - port: 80
    targetPort: 80
  type: ClusterIP  # Other types: NodePort, LoadBalancer, ExternalName`}
        </CodeBlock>
        
        <h4>4. ConfigMaps and Secrets</h4>
        <p>
          ConfigMaps and Secrets let you separate configuration details from your container images:
        </p>
        
        <CodeBlock language="yaml">
{`# Example ConfigMap manifest (configmap.yaml)
apiVersion: v1
kind: ConfigMap
metadata:
  name: app-config
data:
  database_url: "mysql://mysql.default.svc.cluster.local:3306/mydb"
  feature_flags: "search=enabled,notifications=disabled"

# Example Secret manifest (secret.yaml)
apiVersion: v1
kind: Secret
metadata:
  name: app-secrets
type: Opaque
data:
  # Values must be base64 encoded
  db_password: cGFzc3dvcmQxMjM=  # "password123" base64 encoded
  api_key: YWJjZGVmMTIzNDU2Nzg5MA==`}
        </CodeBlock>
        
        <h2>Getting Started with Kubernetes</h2>
        <p>
          Now that you understand the basic concepts, let's see how to get started with Kubernetes.
        </p>
        
        <h3>Setting Up a Local Kubernetes Cluster</h3>
        <p>
          For development and learning, you can set up a local Kubernetes cluster using tools like:
        </p>
        
        <ul>
          <li><strong>Minikube:</strong> A tool that sets up a single-node Kubernetes cluster on your local machine.</li>
          <li><strong>Kind (Kubernetes IN Docker):</strong> Runs Kubernetes clusters using Docker containers as nodes.</li>
          <li><strong>Docker Desktop:</strong> Includes a simple Kubernetes setup for macOS and Windows.</li>
        </ul>
        
        <CodeBlock language="yaml">
{`# Installing Minikube
# For macOS with Homebrew
brew install minikube

# Start Minikube
minikube start

# Verify installation
kubectl get nodes`}
        </CodeBlock>
        
        <h3>Using kubectl: The Kubernetes Command-Line Tool</h3>
        <p>
          kubectl is the command-line tool for interacting with Kubernetes clusters. Here are some essential commands:
        </p>
        
        <CodeBlock language="yaml">
{`# Create resources from YAML files
kubectl apply -f deployment.yaml

# List resources
kubectl get pods
kubectl get deployments
kubectl get services

# Describe resources in detail
kubectl describe pod nginx-pod-abc123

# View logs
kubectl logs nginx-pod-abc123

# Execute command in a container
kubectl exec -it nginx-pod-abc123 -- /bin/bash

# Delete resources
kubectl delete -f deployment.yaml
kubectl delete pod nginx-pod-abc123`}
        </CodeBlock>
        
        <h3>Deploying Your First Application</h3>
        <p>
          Let's walk through deploying a simple web application on Kubernetes:
        </p>
        
        <h4>Step 1: Create a Deployment</h4>
        <CodeBlock language="yaml">
{`# Create a deployment for a simple web app
kubectl create deployment web-app --image=nginx:latest --replicas=2

# Check the status of the deployment
kubectl get deployments
kubectl get pods`}
        </CodeBlock>
        
        <h4>Step 2: Expose the Deployment with a Service</h4>
        <CodeBlock language="yaml">
{`# Expose the deployment as a service
kubectl expose deployment web-app --type=NodePort --port=80

# Check the service
kubectl get services

# For Minikube, get the URL to access the service
minikube service web-app --url`}
        </CodeBlock>
        
        <h4>Step 3: Scale the Application</h4>
        <CodeBlock language="yaml">
{`# Scale to more replicas
kubectl scale deployment web-app --replicas=4

# Verify the scaling
kubectl get pods`}
        </CodeBlock>
        
        <h4>Step 4: Update the Application</h4>
        <CodeBlock language="yaml">
{`# Update the container image
kubectl set image deployment/web-app nginx=nginx:1.21

# Check the rollout status
kubectl rollout status deployment/web-app

# If needed, rollback to previous version
kubectl rollout undo deployment/web-app`}
        </CodeBlock>
        
        <h2>Kubernetes Networking Basics</h2>
        <p>
          Kubernetes has a sophisticated networking model that enables communication between pods, services, 
          and external clients.
        </p>
        
        <h3>Pod-to-Pod Communication</h3>
        <p>
          Kubernetes assigns each Pod an IP address, and Pods can communicate directly with each other 
          using these IPs within the cluster. This is made possible by a cluster network that connects all nodes.
        </p>
        
        <h3>Service Types</h3>
        <p>
          Kubernetes supports several types of Services for different exposure scenarios:
        </p>
        
        <ul>
          <li><strong>ClusterIP:</strong> Exposes the Service on an internal IP. Only accessible within the cluster.</li>
          <li><strong>NodePort:</strong> Exposes the Service on each Node's IP at a static port. Accessible from outside the cluster.</li>
          <li><strong>LoadBalancer:</strong> Creates an external load balancer and assigns a fixed, external IP to the Service.</li>
          <li><strong>ExternalName:</strong> Maps the Service to a DNS name rather than a selector.</li>
        </ul>
        
        <h3>Ingress Controllers</h3>
        <p>
          For more sophisticated HTTP routing, Kubernetes provides Ingress resources that manage external 
          access to services in a cluster, typically HTTP.
        </p>
        
        <CodeBlock language="yaml">
{`# Example Ingress resource
apiVersion: networking.k8s.io/v1
kind: Ingress
metadata:
  name: example-ingress
spec:
  rules:
  - host: example.com
    http:
      paths:
      - path: /
        pathType: Prefix
        backend:
          service:
            name: web-app
            port:
              number: 80
  - host: api.example.com
    http:
      paths:
      - path: /
        pathType: Prefix
        backend:
          service:
            name: api-service
            port:
              number: 8080`}
        </CodeBlock>
        
        <h2>Working with Storage in Kubernetes</h2>
        <p>
          Applications often need persistent storage. Kubernetes provides several abstractions to handle storage needs.
        </p>
        
        <h3>Volumes</h3>
        <p>
          Volumes are directory structures containing data accessible to containers in a Pod. 
          They exist as long as the Pod exists.
        </p>
        
        <h3>Persistent Volumes (PV) and Persistent Volume Claims (PVC)</h3>
        <p>
          For storage that exists independently of Pods:
        </p>
        
        <CodeBlock language="yaml">
{`# Example PersistentVolume
apiVersion: v1
kind: PersistentVolume
metadata:
  name: pv-storage
spec:
  capacity:
    storage: 10Gi
  accessModes:
    - ReadWriteOnce
  persistentVolumeReclaimPolicy: Retain
  storageClassName: standard
  hostPath:
    path: "/mnt/data"

# Example PersistentVolumeClaim
apiVersion: v1
kind: PersistentVolumeClaim
metadata:
  name: pvc-claim
spec:
  accessModes:
    - ReadWriteOnce
  resources:
    requests:
      storage: 5Gi
  storageClassName: standard

# Using PVC in a Pod
apiVersion: v1
kind: Pod
metadata:
  name: db-pod
spec:
  containers:
  - name: mysql
    image: mysql:5.7
    volumeMounts:
    - mountPath: "/var/lib/mysql"
      name: mysql-data
  volumes:
  - name: mysql-data
    persistentVolumeClaim:
      claimName: pvc-claim`}
        </CodeBlock>
        
        <h3>Storage Classes</h3>
        <p>
          StorageClasses allow administrators to define different "classes" of storage with different properties.
        </p>
        
        <CodeBlock language="yaml">
{`# Example StorageClass for cloud provider
apiVersion: storage.k8s.io/v1
kind: StorageClass
metadata:
  name: fast
provisioner: kubernetes.io/aws-ebs
parameters:
  type: gp2
  fsType: ext4
reclaimPolicy: Delete
volumeBindingMode: Immediate`}
        </CodeBlock>
        
        <h2>Managing Application Configuration</h2>
        <p>
          Kubernetes provides several ways to configure your applications without rebuilding container images.
        </p>
        
        <h3>Using ConfigMaps for Configuration Data</h3>
        <p>
          ConfigMaps allow you to decouple configuration details from image content:
        </p>
        
        <CodeBlock language="yaml">
{`# Create ConfigMap
kubectl create configmap app-config --from-literal=database_url=mysql://mysql:3306/mydb --from-file=config.properties

# Using ConfigMap as environment variables
apiVersion: v1
kind: Pod
metadata:
  name: app-pod
spec:
  containers:
  - name: app
    image: myapp:1.0
    envFrom:
    - configMapRef:
        name: app-config

# Using ConfigMap as a volume
apiVersion: v1
kind: Pod
metadata:
  name: app-pod
spec:
  containers:
  - name: app
    image: myapp:1.0
    volumeMounts:
    - name: config-volume
      mountPath: /etc/config
  volumes:
  - name: config-volume
    configMap:
      name: app-config`}
        </CodeBlock>
        
        <h3>Managing Secrets</h3>
        <p>
          For sensitive information, Kubernetes provides Secrets:
        </p>
        
        <CodeBlock language="yaml">
{`# Create a Secret
kubectl create secret generic db-credentials --from-literal=username=admin --from-literal=password=secret

# Using Secret as environment variables
apiVersion: v1
kind: Pod
metadata:
  name: app-pod
spec:
  containers:
  - name: app
    image: myapp:1.0
    env:
    - name: DB_USERNAME
      valueFrom:
        secretKeyRef:
          name: db-credentials
          key: username
    - name: DB_PASSWORD
      valueFrom:
        secretKeyRef:
          name: db-credentials
          key: password`}
        </CodeBlock>
        
        <h2>Monitoring and Logging in Kubernetes</h2>
        <p>
          Understanding what's happening in your Kubernetes cluster is crucial for operations.
        </p>
        
        <h3>Basic Monitoring with kubectl</h3>
        <CodeBlock language="yaml">
{`# Get resource usage of pods
kubectl top pod

# Get resource usage of nodes
kubectl top node

# View logs for a pod
kubectl logs my-pod

# View logs for a specific container in a pod
kubectl logs my-pod -c my-container

# Stream logs in real-time
kubectl logs -f my-pod

# View events in the cluster
kubectl get events`}
        </CodeBlock>
        
        <h3>Advanced Monitoring Solutions</h3>
        <p>
          For production environments, consider using:
        </p>
        <ul>
          <li><strong>Prometheus:</strong> For monitoring and alerting</li>
          <li><strong>Grafana:</strong> For visualization of metrics</li>
          <li><strong>Elasticsearch, Fluentd, and Kibana (EFK Stack):</strong> For centralized logging</li>
          <li><strong>Kubernetes Dashboard:</strong> For a web-based UI to manage and troubleshoot the cluster</li>
        </ul>
        
        <h2>Common Kubernetes Patterns</h2>
        <p>
          As you become more familiar with Kubernetes, you'll encounter certain patterns that address common deployment scenarios.
        </p>
        
        <h3>Sidecar Pattern</h3>
        <p>
          Add a helper container to a Pod to extend or enhance the main container:
        </p>
        
        <CodeBlock language="yaml">
{`apiVersion: v1
kind: Pod
metadata:
  name: app-with-sidecar
spec:
  containers:
  - name: app
    image: myapp:1.0
  - name: logger-sidecar
    image: logger:1.0
    volumeMounts:
    - name: logs
      mountPath: /var/log/app
  volumes:
  - name: logs
    emptyDir: {}`}
        </CodeBlock>
        
        <h3>Init Containers</h3>
        <p>
          Run setup or initialization logic before the application containers start:
        </p>
        
        <CodeBlock language="yaml">
{`apiVersion: v1
kind: Pod
metadata:
  name: app-with-init
spec:
  initContainers:
  - name: init-db
    image: busybox
    command: ['sh', '-c', 'until nslookup database; do echo waiting for database; sleep 2; done;']
  containers:
  - name: app
    image: myapp:1.0`}
        </CodeBlock>
        
        <h3>Ambassador Pattern</h3>
        <p>
          Provide a proxy to connect to external services:
        </p>
        
        <CodeBlock language="yaml">
{`apiVersion: v1
kind: Pod
metadata:
  name: app-with-ambassador
spec:
  containers:
  - name: app
    image: myapp:1.0
    env:
    - name: DB_URL
      value: "localhost:3306"  # Connect to the ambassador container
  - name: database-ambassador
    image: ambassador:1.0
    ports:
    - containerPort: 3306`}
        </CodeBlock>
        
        <h2>Best Practices for Kubernetes Deployments</h2>
        <p>
          As you start working with Kubernetes in real-world scenarios, consider these best practices:
        </p>
        
        <h3>Resource Management</h3>
        <p>
          Always specify resource requests and limits for containers:
        </p>
        
        <CodeBlock language="yaml">
{`apiVersion: v1
kind: Pod
metadata:
  name: resource-managed-pod
spec:
  containers:
  - name: app
    image: myapp:1.0
    resources:
      requests:
        memory: "64Mi"
        cpu: "250m"
      limits:
        memory: "128Mi"
        cpu: "500m"`}
        </CodeBlock>
        
        <h3>Health Checks</h3>
        <p>
          Implement liveness and readiness probes to ensure proper health monitoring:
        </p>
        
        <CodeBlock language="yaml">
{`apiVersion: v1
kind: Pod
metadata:
  name: health-checked-pod
spec:
  containers:
  - name: app
    image: myapp:1.0
    livenessProbe:
      httpGet:
        path: /health
        port: 8080
      initialDelaySeconds: 15
      periodSeconds: 10
    readinessProbe:
      httpGet:
        path: /ready
        port: 8080
      initialDelaySeconds: 5
      periodSeconds: 5`}
        </CodeBlock>
        
        <h3>Namespace Organization</h3>
        <p>
          Use namespaces to organize and isolate your resources:
        </p>
        
        <CodeBlock language="yaml">
{`# Create a namespace
kubectl create namespace my-application

# Deploy resources to a specific namespace
kubectl apply -f deployment.yaml -n my-application

# View resources in a namespace
kubectl get pods -n my-application`}
        </CodeBlock>
        
        <h2>Conclusion</h2>
        <p>
          Kubernetes represents a powerful system for managing containerized applications at scale. While the learning 
          curve can be steep, understanding the core concepts and components provides a solid foundation for 
          building and managing modern cloud-native applications.
        </p>
        
        <p>
          As you continue your Kubernetes journey, remember that the ecosystem is constantly evolving, with new 
          features and best practices emerging regularly. The basics covered in this article should serve as a 
          stepping stone to more advanced topics like custom resource definitions (CRDs), operators, service meshes, 
          and GitOps workflows.
        </p>
        
        <p>
          Start small, experiment often, and gradually expand your knowledge as you become more comfortable 
          with Kubernetes' capabilities. With practice, you'll be deploying complex applications with confidence 
          and taking full advantage of Kubernetes' powerful orchestration features.
        </p>
      </>
    )
  };

  return (
    <div className="article-page">
      <Navbar />
      <main>
        <Article article={articleData} />
      </main>
      <Footer />
    </div>
  );
}

export default KubernetesBasics;
